import { ExportMenu } from "@/components/ExportMenu";
import { useEffect, useMemo, useState } from "react";
import api, { money, fmtDate, apiErr } from "@/lib/api";
import { withAuthCode, apiErrAny } from "@/lib/authCode";
import { useAuthCodePrompt } from "@/components/AuthCodeModal";
import { toast } from "sonner";
import { useActivity } from "@/context/ActivityContext";
import Modal from "@/components/Modal";
import Pagination from "@/components/Pagination";
import { ShoppingBag, Plus, PackageCheck, Wallet, Undo2, Ban, Trash2, Eye } from "lucide-react";
import ConfirmDialog from "@/components/ConfirmDialog";

const PAY_LABEL = { unpaid: "Non payé", partial: "Partiel", paid: "Soldé" };
const PAY_TONE = { unpaid: "bg-[#FEE2E2] text-[#B91C1C]", partial: "bg-[#FEF3C7] text-[#B45309]", paid: "bg-[#DCFCE7] text-[#15803D]" };
const STATUS_LABEL = { ordered: "Commandé", partial: "Réception partielle", received: "Réceptionné", cancelled: "Annulé" };
const STATUS_TONE = { ordered: "bg-[#E0F2FE] text-[#0369A1]", partial: "bg-[#FEF3C7] text-[#B45309]", received: "bg-[#DCFCE7] text-[#15803D]", cancelled: "bg-[#F1F5F9] text-[#64748B]" };
const EVENT_LABEL = { created: "Création", reception: "Réception", payment: "Paiement", return: "Retour", refund: "Remboursement reçu" };
const EVENT_TONE = { created: "bg-[#E0F2FE] text-[#0369A1]", reception: "bg-[#DCFCE7] text-[#15803D]", payment: "bg-[#EDE9FE] text-[#6D28D9]", return: "bg-[#FEF3C7] text-[#B45309]", refund: "bg-[#DCFCE7] text-[#15803D]" };

export default function Purchases() {
  const { current } = useActivity();
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [supplierId, setSupplierId] = useState("");
  const [expected, setExpected] = useState("");
  const [note, setNote] = useState("");
  const [paid, setPaid] = useState("");
  const [delTarget, setDelTarget] = useState(null);
  const [method, setMethod] = useState("cash");
  const [lines, setLines] = useState([]);
  const [payModal, setPayModal] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [retModal, setRetModal] = useState(null);
  const [retLines, setRetLines] = useState([]);
  const [page, setPage] = useState(1);
  // quick-add supplier
  const [supOpen, setSupOpen] = useState(false);
  const [supName, setSupName] = useState("");
  const [supPhone, setSupPhone] = useState("");
  const [supAddr, setSupAddr] = useState("");
  // detail sheet
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [linePage, setLinePage] = useState(1);
  const [histPage, setHistPage] = useState(1);

  const load = async () => {
    if (!current) return;
    try {
      const [p, s, pr] = await Promise.all([
        api.get(`/purchases?activity_id=${current.id}`),
        api.get(`/parties?kind=supplier&activity_id=${current.id}`),
        api.get(`/products?activity_id=${current.id}`),
      ]);
      setRows(p.data); setSuppliers(s.data); setProducts(pr.data); setPage(1);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail, e) || "Chargement impossible"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [current]);

  const resetForm = () => { setSupplierId(""); setExpected(""); setNote(""); setPaid(""); setMethod("cash"); setLines([]); };
  const addLine = () => setLines((l) => [...l, { key: Date.now() + Math.random(), product_id: "", packaging: "base", quantity: "", unit_cost: "" }]);
  const setLine = (key, field, val) => setLines((l) => l.map((x) => x.key === key ? { ...x, [field]: val } : x));
  const rmLine = (key) => setLines((l) => l.filter((x) => x.key !== key));

  const prodById = (id) => products.find((p) => p.id === id);
  const setLineProduct = (key, pid) => {
    const pr = prodById(pid);
    setLines((l) => l.map((x) => x.key === key ? { ...x, product_id: pid, packaging: "base", unit_cost: pr ? pr.cost : "" } : x));
  };
  const setLinePkg = (key, pkgName) => {
    setLines((l) => l.map((x) => {
      if (x.key !== key) return x;
      const pr = prodById(x.product_id);
      let cost = pr ? pr.cost : "";
      if (pkgName !== "base" && pr) { const pk = (pr.packagings || []).find((p) => p.name === pkgName); if (pk) cost = pk.cost != null ? pk.cost : ""; }
      return { ...x, packaging: pkgName, unit_cost: cost };
    }));
  };

  const total = useMemo(() => lines.reduce((s, l) => s + Number(l.unit_cost || 0) * Number(l.quantity || 0), 0), [lines]);
  const authPrompt = useAuthCodePrompt();

  const save = async () => {
    const valid = lines.filter((l) => l.product_id && Number(l.quantity) > 0);
    if (valid.length === 0) return toast.error("Ajoutez au moins un article");
    try {
      const body = {
        activity_id: current.id, supplier_id: supplierId || null,
        lines: valid.map((l) => ({ product_id: l.product_id, packaging: l.packaging || "base", quantity: Number(l.quantity), unit_cost: Number(catalogCost(l) || l.unit_cost || 0),
          negotiated_cost: catalogCost(l) != null && Math.abs(Number(l.unit_cost || 0) - Number(catalogCost(l))) > 0.5 ? Number(l.unit_cost || 0) : undefined })),
        paid: Number(paid || 0), method, account: method === "cash" ? "Espèces" : method,
        expected_date: expected || null, note: note || null,
      };
      await withAuthCode((code) => api.post("/purchases", code ? { ...body, auth_code: code } : body), authPrompt.ask);
      toast.success("Commande fournisseur créée"); setOpen(false); resetForm(); load();
    } catch (e) { if (!e.cancelled) toast.error(apiErrAny(e, apiErr)); }
  };
  // Prix d'achat catalogue de la présentation (pour signaler un prix négocié sans toucher au catalogue)
  const catalogCost = (l) => { const pr = prodById(l.product_id); if (!pr) return null; if (!l.packaging || l.packaging === "base") return Number(pr.cost || 0); const pk = (pr.packagings || []).find((p) => p.name === l.packaging); return pk && pk.cost != null ? Number(pk.cost) : Number(pk?.factor || 1) * Number(pr.cost || 0); };

  // Ajout rapide fournisseur — conserve les lignes déjà saisies
  const saveSupplier = async () => {
    if (!supName.trim()) return toast.error("Le nom du fournisseur est obligatoire");
    try {
      const { data } = await api.post("/parties", { name: supName.trim(), kind: "supplier", phone: supPhone || null, address: supAddr || null, activity_id: current.id });
      setSuppliers((s) => [...s, data].sort((a, b) => a.name.localeCompare(b.name)));
      setSupplierId(data.id);
      setSupOpen(false); setSupName(""); setSupPhone(""); setSupAddr("");
      toast.success("Fournisseur ajouté et sélectionné");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const [recvModal, setRecvModal] = useState(null);
  const [recvLines, setRecvLines] = useState([]);
  const [refundModal, setRefundModal] = useState(null);
  const [refundAmt, setRefundAmt] = useState("");
  const openReceive = (p) => { setRecvModal(p); setRecvLines(p.lines.map((l) => { const rem = (l.pack_qty != null ? l.pack_qty : l.quantity) - (l.received_pack || 0); return { ...l, remaining: rem, recv: String(rem) }; })); };
  const submitReceive = async (all) => {
    try {
      const body = all ? {} : { lines: recvLines.filter((l) => Number(l.recv) > 0).map((l) => ({ product_id: l.product_id, quantity: Number(l.recv) })) };
      await api.post(`/purchases/${recvModal.id}/receive`, body);
      toast.success("Réception enregistrée · stock mis à jour"); setRecvModal(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const openRefund = (p) => { setRefundModal(p); setRefundAmt(String(p.supplier_credit || 0)); };
  const doRefund = async () => {
    try { await api.post(`/purchases/${refundModal.id}/supplier-refund`, { amount: Number(refundAmt || 0), method: "cash", account: "Espèces" }); toast.success("Remboursement fournisseur encaissé"); setRefundModal(null); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const cancel = async (id) => { try { await api.post(`/purchases/${id}/cancel`); toast.success("Commande annulée"); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const del = async () => { try { await api.delete(`/purchases/${delTarget.id}`); toast.success("Commande supprimée"); setDelTarget(null); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); setDelTarget(null); } };

  const openPay = (p) => { setPayModal(p); setPayAmount(String(p.balance)); };
  const doPay = async () => {
    try {
      await api.post(`/purchases/${payModal.id}/pay`, { amount: Number(payAmount || 0), method: "cash", account: "Espèces" });
      toast.success("Paiement fournisseur enregistré"); setPayModal(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const openReturn = (p) => { setRetModal(p); setRetLines(p.lines.map((l) => ({ ...l, ret: "" }))); };
  const doReturn = async () => {
    const valid = retLines.filter((l) => Number(l.ret) > 0);
    if (valid.length === 0) return toast.error("Indiquez les quantités à retourner");
    try {
      await api.post(`/purchases/${retModal.id}/return`, {
        lines: valid.map((l) => ({ product_id: l.product_id, product_name: l.product_name, quantity: Number(l.ret), unit_cost: Number(l.base_cost != null ? l.base_cost : l.unit_cost) })),
        reduce_debt: true,
      });
      toast.success("Retour fournisseur enregistré · stock ajusté"); setRetModal(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  // Détails
  const openDetail = async (p) => {
    setDetail({ purchase: p, lines: [], finance: null, history: [] }); setLinePage(1); setHistPage(1); setDetailLoading(true);
    try { const { data } = await api.get(`/purchases/${p.id}/detail`); setDetail(data); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); setDetail(null); }
    finally { setDetailLoading(false); }
  };
  // action contextuelle depuis la fiche → ferme la fiche puis ouvre le modal d'action
  const detailAction = (fn) => { const p = detail.purchase; setDetail(null); setTimeout(() => fn(p), 60); };

  if (!current) return <div className="text-[#64748B]">Sélectionnez une activité.</div>;
  const totalDebt = rows.filter((r) => r.status !== "cancelled").reduce((s, r) => s + (r.balance || 0), 0);
  const paged = rows.slice((page - 1) * 10, page * 10);

  const StatusBadges = ({ p }) => (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_TONE[p.status]}`}>{STATUS_LABEL[p.status] || p.status}</span>
      {p.status !== "cancelled" && <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${PAY_TONE[p.pay_status]}`}>{PAY_LABEL[p.pay_status]}</span>}
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-head text-[#16324F] tracking-tight">Achats & Fournisseurs</h1>
          {authPrompt.modal}
          <p className="text-sm text-[#64748B] mt-1">Commande → réception (stock) → paiement · Dette fournisseur : <span className="font-semibold text-[#B91C1C]">{money(totalDebt)}</span> · {current.name}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ExportMenu dataset="supplier_debts" params={{ activity_id: current.id }} />
          <button onClick={() => { resetForm(); addLine(); setOpen(true); }} className="btn-primary" data-testid="new-purchase-btn"><Plus className="w-4 h-4" /> Nouvelle commande</button>
        </div>
      </div>

      <div className="card">
        {/* Liste en cartes synthétiques (desktop + mobile) */}
        <div className="divide-y divide-[#E2E8F0]">
          {paged.map((p) => {
            const net = p.net_amount != null ? p.net_amount : (p.total - (p.returned_value || 0));
            return (
            <div key={p.id} className={`py-4 first:pt-1 ${p.status === "cancelled" ? "opacity-60" : ""}`} data-testid={`purchase-card-${p.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#0F172A] truncate">{p.supplier_name}</span>
                    <span className="text-xs text-[#94A3B8] font-mono">{p.number}</span>
                  </div>
                  <div className="text-xs text-[#94A3B8] mt-0.5">{fmtDate(p.created_at)}</div>
                  <div className="mt-2"><StatusBadges p={p} /></div>
                </div>
                <div className="flex flex-col sm:items-end gap-1 shrink-0">
                  {(p.status === "ordered" || p.status === "partial") && <div className="text-[11px] text-[#0369A1]" data-testid={`engagement-${p.id}`}>Engagement {money(p.total)}{p.status === "partial" ? " · reçu en partie" : " · non reçu (aucune dette)"}</div>}
                  <div className="text-sm text-[#64748B]">Net <span className="font-bold text-[#0F172A]">{money(net)}</span>{(p.returned_value || 0) > 0 && <span className="text-[11px] text-[#B45309] ml-1">(retours −{money(p.returned_value)})</span>}</div>
                  <div className="text-sm text-[#15803D]">Payé {money(p.paid)}</div>
                  {p.balance > 0 && <div className="text-sm text-[#B91C1C] font-semibold">Dette {money(p.balance)}</div>}
                  {(p.supplier_credit || 0) > 0 && <div className="text-sm text-[#15803D] font-semibold">Avoir {money(p.supplier_credit)}</div>}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={() => openDetail(p)} className="btn-outline !py-2 !px-3" data-testid={`detail-purchase-${p.id}`}><Eye className="w-4 h-4" /> Détails</button>
                {(p.status === "ordered" || p.status === "partial") && <button onClick={() => openReceive(p)} className="btn-outline !py-2 !px-3 !text-[#15803D]" data-testid={`receive-purchase-${p.id}`}><PackageCheck className="w-4 h-4" /> Réceptionner</button>}
                {p.status !== "cancelled" && p.balance > 0 && <button onClick={() => openPay(p)} className="btn-outline !py-2 !px-3" data-testid={`pay-purchase-${p.id}`}><Wallet className="w-4 h-4" /> Payer</button>}
              </div>
            </div>
          );})}
          {rows.length === 0 && <div className="text-center py-10 text-[#94A3B8]">Aucune commande fournisseur</div>}
        </div>
        <Pagination page={page} setPage={setPage} total={rows.length} />

      <ConfirmDialog open={!!delTarget} danger title="Supprimer la commande" message={`Supprimer définitivement la commande ${delTarget?.number || ""} ? Cette action est irréversible.`} confirmLabel="Supprimer" onConfirm={del} onClose={() => setDelTarget(null)} />
      </div>

      {/* New purchase */}
      <Modal open={open} onClose={() => { setOpen(false); resetForm(); }} title="Nouvelle commande fournisseur" wide testId="purchase-modal"
        footer={<><button className="btn-outline" onClick={() => { setOpen(false); resetForm(); }}>Annuler</button><button className="btn-primary" onClick={save} data-testid="save-purchase-btn">Enregistrer la commande</button></>}>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className="lbl">Fournisseur</label>
              <div className="flex gap-2">
                <select className="inp flex-1" value={supplierId} onChange={(e) => setSupplierId(e.target.value)} data-testid="purchase-supplier-select">
                  <option value="">Fournisseur comptant</option>
                  {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <button type="button" onClick={() => setSupOpen(true)} className="btn-outline !px-3 shrink-0" title="Ajouter un fournisseur" data-testid="quick-add-supplier-btn"><Plus className="w-4 h-4" /></button>
              </div>
            </div>
            <div><label className="lbl">Date prévue</label><input type="date" className="inp" value={expected} onChange={(e) => setExpected(e.target.value)} data-testid="purchase-expected-input" /></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2"><label className="lbl !mb-0">Articles</label><button type="button" onClick={addLine} className="btn-outline !py-1 !px-2.5 text-xs" data-testid="purchase-add-line-btn"><Plus className="w-3.5 h-3.5" /> Ligne</button></div>
            <div className="space-y-2">
              {lines.map((l) => {
                const pr = prodById(l.product_id);
                return (
                <div key={l.key} data-testid="purchase-line">
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-4"><select className="inp" value={l.product_id} onChange={(e) => setLineProduct(l.key, e.target.value)} data-testid="purchase-line-product"><option value="">Produit…</option>{products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
                    <div className="col-span-3"><select className="inp" value={l.packaging} onChange={(e) => setLinePkg(l.key, e.target.value)} data-testid="purchase-line-packaging"><option value="base">{pr?.base_unit || "unité"} (unité)</option>{(pr?.packagings || []).map((pk) => <option key={pk.name} value={pk.name}>{pk.name} (x{pk.factor})</option>)}</select></div>
                    <div className="col-span-2"><input type="number" className="inp" placeholder="Qté" value={l.quantity} onChange={(e) => setLine(l.key, "quantity", e.target.value)} data-testid="purchase-line-qty" /></div>
                    <div className="col-span-2"><input type="number" className="inp" placeholder="Coût" value={l.unit_cost} onChange={(e) => setLine(l.key, "unit_cost", e.target.value)} data-testid="purchase-line-cost" /></div>
                    <div className="col-span-1"><button type="button" onClick={() => rmLine(l.key)} className="text-[#B91C1C] p-2"><Trash2 className="w-4 h-4" /></button></div>
                  </div>
                  {Number(l.quantity) > 0 && Number(l.unit_cost) > 0 && (
                    <div className="text-right text-xs text-[#64748B] mt-1 pr-10" data-testid="purchase-line-subtotal">
                      {catalogCost(l) != null && Math.abs(Number(l.unit_cost) - catalogCost(l)) > 0.5 && <span className="mr-2 px-1.5 rounded bg-[#FEF3C7] text-[#92400E] font-semibold" data-testid="purchase-line-negotiated">Prix négocié · catalogue {money(catalogCost(l))} → {money(Number(l.unit_cost))}</span>}
                      Sous-total : <span className="font-semibold text-[#16324F]">{money(Number(l.unit_cost) * Number(l.quantity))}</span></div>
                  )}
                </div>
                );
              })}
              {lines.length === 0 && <div className="text-sm text-[#94A3B8] py-2">Aucune ligne. Cliquez sur « Ligne ».</div>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div><label className="lbl">Acompte payé</label><input type="number" className="inp" value={paid} onChange={(e) => setPaid(e.target.value)} placeholder="0" data-testid="purchase-paid-input" /></div>
            <div><label className="lbl">Mode</label><select className="inp" value={method} onChange={(e) => setMethod(e.target.value)}><option value="cash">Espèces</option><option value="mobile_money">Mobile Money</option><option value="bank">Banque</option></select></div>
            <div className="text-right"><div className="lbl">Total commande</div><div className="text-xl font-bold font-head text-[#16324F]" data-testid="purchase-total">{money(total)}</div></div>
          </div>
          <div><label className="lbl">Note</label><input className="inp" value={note} onChange={(e) => setNote(e.target.value)} /></div>
        </div>
      </Modal>

      {/* Quick-add supplier */}
      <Modal open={supOpen} onClose={() => setSupOpen(false)} title="Nouveau fournisseur" zClass="z-[60]" testId="quick-supplier-modal"
        footer={<><button className="btn-outline" onClick={() => setSupOpen(false)}>Annuler</button><button className="btn-primary" onClick={saveSupplier} data-testid="save-supplier-btn">Enregistrer</button></>}>
        <div className="space-y-3">
          <div><label className="lbl">Nom <span className="text-[#B91C1C]">*</span></label><input className="inp" value={supName} onChange={(e) => setSupName(e.target.value)} placeholder="Nom du fournisseur" data-testid="supplier-name-input" autoFocus /></div>
          <div><label className="lbl">Téléphone (facultatif)</label><input className="inp" value={supPhone} onChange={(e) => setSupPhone(e.target.value)} data-testid="supplier-phone-input" /></div>
          <div><label className="lbl">Adresse (facultatif)</label><input className="inp" value={supAddr} onChange={(e) => setSupAddr(e.target.value)} data-testid="supplier-address-input" /></div>
        </div>
      </Modal>

      {/* Détails de la commande */}
      <Modal open={!!detail} onClose={() => setDetail(null)} title={`Commande ${detail?.purchase?.number || ""}`} wide testId="purchase-detail-modal"
        footer={<button className="btn-outline" onClick={() => setDetail(null)}>Fermer</button>}>
        {detail && (
          detailLoading ? <div className="py-10 text-center text-[#94A3B8]">Chargement…</div> : (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-semibold text-[#0F172A]">{detail.purchase.supplier_name}</div>
                <div className="text-xs text-[#94A3B8]">{fmtDate(detail.purchase.created_at)}</div>
              </div>
              <StatusBadges p={detail.purchase} />
            </div>

            {/* Actions contextuelles */}
            <div className="flex flex-wrap gap-2" data-testid="detail-actions">
              {(detail.purchase.status === "ordered" || detail.purchase.status === "partial") && <button onClick={() => detailAction(openReceive)} className="btn-outline !py-1.5 !px-3 !text-[#15803D]" data-testid="detail-receive-btn"><PackageCheck className="w-4 h-4" /> Réceptionner</button>}
              {detail.purchase.status !== "cancelled" && detail.purchase.balance > 0 && <button onClick={() => detailAction(openPay)} className="btn-outline !py-1.5 !px-3" data-testid="detail-pay-btn"><Wallet className="w-4 h-4" /> Payer</button>}
              {(detail.purchase.status === "received" || detail.purchase.status === "partial") && <button onClick={() => detailAction(openReturn)} className="btn-outline !py-1.5 !px-3 !text-[#B45309]" data-testid="detail-return-btn"><Undo2 className="w-4 h-4" /> Retourner</button>}
              {(detail.finance?.supplier_credit || 0) > 0 && <button onClick={() => detailAction(openRefund)} className="btn-outline !py-1.5 !px-3 !text-[#15803D]" data-testid="detail-refund-btn"><Wallet className="w-4 h-4" /> Remboursement reçu</button>}
              {detail.purchase.status === "ordered" && <button onClick={() => detailAction((p) => cancel(p.id))} className="btn-outline !py-1.5 !px-3 !text-[#B91C1C]" data-testid="detail-cancel-btn"><Ban className="w-4 h-4" /> Annuler</button>}
              {!detail.purchase.received && (detail.purchase.status === "ordered" || detail.purchase.status === "cancelled") && <button onClick={() => detailAction((p) => setDelTarget(p))} className="btn-outline !py-1.5 !px-3 !text-[#B91C1C]" data-testid="detail-delete-btn"><Trash2 className="w-4 h-4" /> Supprimer</button>}
            </div>

            {/* Articles */}
            <div>
              <div className="lbl mb-2">Articles</div>
              <div className="overflow-x-auto -mx-1">
                <table className="w-full text-sm min-w-[560px]">
                  <thead><tr className="text-[11px] text-[#94A3B8] uppercase">
                    <th className="th">Produit</th><th className="th">Unité</th><th className="th text-right">Cmd.</th>
                    <th className="th text-right">Reçu</th><th className="th text-right">Retour</th><th className="th text-right">Restant</th>
                    <th className="th text-right">Coût U.</th><th className="th text-right">S-total init.</th><th className="th text-right">Après retour</th>
                  </tr></thead>
                  <tbody>
                    {detail.lines.slice((linePage - 1) * 10, linePage * 10).map((l, i) => (
                      <tr key={i} className="row" data-testid={`detail-line-${i}`}>
                        <td className="td font-medium">{l.product_name}</td>
                        <td className="td text-xs text-[#64748B]">{l.packaging === "base" ? "unité" : `${l.packaging} (x${l.factor})`}</td>
                        <td className="td text-right">{l.ordered_base}</td>
                        <td className="td text-right text-[#15803D]">{l.received_base}</td>
                        <td className="td text-right text-[#B45309]">{l.returned_base || 0}</td>
                        <td className="td text-right">{l.remaining_base}</td>
                        <td className="td text-right">{money(l.base_cost)}</td>
                        <td className="td text-right">{money(l.subtotal_initial)}</td>
                        <td className="td text-right font-semibold">{money(l.subtotal_after_return)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination page={linePage} setPage={setLinePage} total={detail.lines.length} />
            </div>

            {/* Situation financière */}
            <div>
              <div className="lbl mb-2">Situation financière</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="detail-finance">
                <div className="rounded-lg bg-[#F8FAFC] p-3"><div className="text-[11px] text-[#94A3B8]">Total initial</div><div className="font-semibold text-[#0F172A]">{money(detail.finance.total_initial)}</div></div>
                <div className="rounded-lg bg-[#F8FAFC] p-3"><div className="text-[11px] text-[#94A3B8]">Retours</div><div className="font-semibold text-[#B45309]">−{money(detail.finance.returned_value)}</div></div>
                <div className="rounded-lg bg-[#F8FAFC] p-3"><div className="text-[11px] text-[#94A3B8]">Achat net</div><div className="font-semibold text-[#0F172A]">{money(detail.finance.net_amount)}</div></div>
                <div className="rounded-lg bg-[#F8FAFC] p-3"><div className="text-[11px] text-[#94A3B8]">Payé</div><div className="font-semibold text-[#15803D]">{money(detail.finance.paid)}</div></div>
                <div className="rounded-lg bg-[#F8FAFC] p-3"><div className="text-[11px] text-[#94A3B8]">Dette</div><div className="font-semibold text-[#B91C1C]">{money(detail.finance.balance)}</div></div>
                <div className="rounded-lg bg-[#F8FAFC] p-3"><div className="text-[11px] text-[#94A3B8]">Avoir / remb. dû</div><div className="font-semibold text-[#15803D]">{money(detail.finance.supplier_credit)}</div></div>
              </div>
            </div>

            {/* Historique */}
            <div>
              <div className="lbl mb-2">Historique</div>
              <div className="space-y-2" data-testid="detail-history">
                {detail.history.slice((histPage - 1) * 10, histPage * 10).map((e, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm" data-testid={`history-event-${i}`}>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${EVENT_TONE[e.type] || "bg-[#F1F5F9] text-[#64748B]"}`}>{EVENT_LABEL[e.type] || e.type}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[#0F172A]">
                        {e.label}
                        {e.product_name && <span className="text-[#64748B]"> · {e.product_name}{e.qty != null ? ` (${e.qty})` : ""}</span>}
                        {e.amount != null && <span className="font-semibold"> · {money(e.amount)}</span>}
                      </div>
                      <div className="text-[11px] text-[#94A3B8]">{fmtDate(e.at)}{e.actor ? ` · ${e.actor}` : ""}</div>
                    </div>
                  </div>
                ))}
                {detail.history.length === 0 && <div className="text-sm text-[#94A3B8]">Aucun évènement.</div>}
              </div>
              <Pagination page={histPage} setPage={setHistPage} total={detail.history.length} />
            </div>

            {detail.purchase.note && <div className="text-sm text-[#64748B]">Note : {detail.purchase.note}</div>}
          </div>
          )
        )}
      </Modal>

      {/* Pay */}
      <Modal open={!!payModal} onClose={() => setPayModal(null)} title={`Paiement fournisseur ${payModal?.number || ""}`} testId="purchase-pay-modal"
        footer={<><button className="btn-outline" onClick={() => setPayModal(null)}>Annuler</button><button className="btn-primary" onClick={doPay} data-testid="confirm-pay-btn">Payer</button></>}>
        {payModal && (
          <div className="space-y-3">
            <div className="text-sm text-[#475569]">Solde dû : <span className="font-semibold text-[#B91C1C]">{money(payModal.balance)}</span></div>
            <div><label className="lbl">Montant à payer</label><input type="number" className="inp" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} data-testid="pay-amount-input" /></div>
          </div>
        )}
      </Modal>

      {/* Return */}
      <Modal open={!!retModal} onClose={() => setRetModal(null)} title={`Retour fournisseur ${retModal?.number || ""}`} wide testId="purchase-return-modal"
        footer={<><button className="btn-outline" onClick={() => setRetModal(null)}>Annuler</button><button className="btn-primary" onClick={doReturn} data-testid="confirm-return-btn">Enregistrer le retour</button></>}>
        {retModal && (
          <div className="space-y-2">
            <p className="text-sm text-[#64748B]">Indiquez les quantités retournées. Le stock est diminué et la dette fournisseur réduite.</p>
            {retLines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid="return-line">
                <div className="col-span-6 text-sm font-medium">{l.product_name}</div>
                <div className="col-span-3 text-xs text-[#64748B]">reçu : {l.quantity} · {money(l.base_cost != null ? l.base_cost : l.unit_cost)}</div>
                <div className="col-span-3"><input type="number" className="inp !py-1.5" placeholder="Qté retour" value={l.ret} max={l.quantity} onChange={(e) => setRetLines((ls) => ls.map((x, j) => j === i ? { ...x, ret: e.target.value } : x))} data-testid="return-qty-input" /></div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal open={!!recvModal} onClose={() => setRecvModal(null)} title={`Réception ${recvModal?.number || ""}`} wide testId="purchase-receive-modal"
        footer={<><button className="btn-outline" onClick={() => submitReceive(true)} data-testid="receive-all-btn">Tout recevoir</button><button className="btn-primary" onClick={() => submitReceive(false)} data-testid="confirm-receive-btn">Enregistrer</button></>}>
        {recvModal && (
          <div className="space-y-2">
            <p className="text-sm text-[#64748B]">Quantités reçues (en unités de commande). Le stock augmente uniquement des quantités réceptionnées.</p>
            {recvLines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-6 text-sm font-medium">{l.product_name}{l.packaging && l.packaging !== "base" ? ` · ${l.packaging}` : ""}</div>
                <div className="col-span-3 text-xs text-[#64748B]">reste : {l.remaining}</div>
                <div className="col-span-3"><input type="text" inputMode="decimal" className="inp !py-1.5" value={l.recv} disabled={l.remaining <= 0} onChange={(e) => setRecvLines((ls) => ls.map((x, j) => j === i ? { ...x, recv: e.target.value } : x))} data-testid={`receive-qty-${i}`} /></div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal open={!!refundModal} onClose={() => setRefundModal(null)} title={`Remboursement fournisseur ${refundModal?.number || ""}`} testId="purchase-refund-modal"
        footer={<><button className="btn-outline" onClick={() => setRefundModal(null)}>Annuler</button><button className="btn-primary" onClick={doRefund} data-testid="confirm-refund-btn">Encaisser</button></>}>
        {refundModal && (
          <div className="space-y-3">
            <div className="text-sm text-[#475569]">Avoir fournisseur : <span className="font-semibold text-[#15803D]">{money(refundModal.supplier_credit)}</span></div>
            <div><label className="lbl">Montant remboursé reçu</label><input type="number" className="inp" value={refundAmt} onChange={(e) => setRefundAmt(e.target.value)} data-testid="refund-amount-input" /></div>
            <p className="text-xs text-[#94A3B8]">Enregistré comme entrée de caisse (pas une vente).</p>
          </div>
        )}
      </Modal>
    </div>
  );
}
